/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect, Fragment } from 'react';

import { Link } from 'react-router-dom';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import classnames from 'classnames';
import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import {
  Modal, Button, Alert, Row, Col, TabContent, TabPane,
  Nav, NavItem, NavLink, Table, Badge,
} from 'reactstrap';

import { get, post } from '../../helpers/api_helper';
import Inspector from '../Item/inspector';
import FamilyReturnLabelModal from './FamilyReturnLabelModal';

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const buildPackageContentGroups = (contents, currentSaleId = null) => {
  const groups = new Map();

  contents.forEach((row) => {
    const listing = row.order_export_item.sale_item?.listing;
    const saleItemId = row.order_export_item.sale_item?.id;
    const contentSale = row.order_export_item.sale_item?.sale;
    const contentSaleId = row.sale_id || contentSale?.id || null;
    const contentSaleReference = contentSale?.reference1 || `#${contentSaleId}`;
    const { item } = row.order_export_item;
    const trackingNumber = row.package_tracking_number;
    let key = `export-${row.order_export_item.id}`;

    if (item?.id) {
      key = `item-${item.id}`;
    }

    if (saleItemId) {
      key = `sale-item-${saleItemId}`;
    }

    if (listing?.id) {
      key = `listing-${listing.id}`;
    }

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        listing,
        saleItemId,
        saleId: contentSaleId,
        saleReference: contentSaleReference,
        isCurrentSale: currentSaleId !== null && Number(contentSaleId) === Number(currentSaleId),
        parts: [],
        qty: 0,
        subQty: 0,
        trackingNumbers: [],
        valueUsd: 0,
      });
    }

    const group = groups.get(key);
    const qty = Number(row.item_qty) || 0;
    const valueUsd = Number(row.item_value_usd) || 0;
    const itemDescription = row.order_export_item.item_id !== null
      ? `${row.order_export_item.item.manufacturer} ${row.order_export_item.item.part_number} - ${row.order_export_item.item.condition}`
      : row.item_description;
    const itemSummary = row.order_export_item.item_id !== null
      ? `${row.order_export_item.item.manufacturer} ${row.order_export_item.item.part_number}`
      : row.item_description;
    const componentItemId = row.order_export_item.original_substituted_item_id || item?.id || null;
    const partKey = [
      componentItemId || item?.id || itemDescription || row.order_export_item.id,
      item?.id || 'no-item',
      row.order_export_item.original_substituted_item_id || 'original',
    ].join(':');
    let part = group.parts.find((entry) => entry.key === partKey);

    if (part === undefined) {
      part = {
        key: partKey,
        itemId: item?.id || null,
        componentItemId,
        description: itemDescription || 'N/A',
        summary: itemSummary || 'N/A',
        lots: {},
        qty: 0,
        subQty: 0,
      };
      group.parts.push(part);
    }

    const isSub = row.order_export_item.original_substituted_item_id !== null;

    group.qty += qty;
    group.valueUsd += valueUsd;
    part.qty += qty;
    if (isSub) {
      group.subQty += qty;
      part.subQty += qty;
    }

    if (trackingNumber && !group.trackingNumbers.includes(trackingNumber)) {
      group.trackingNumbers.push(trackingNumber);
    }

    if (row.lot) {
      part.lots[row.lot] = (part.lots[row.lot] || 0) + (qty || 1);
    }
  });

  return Array.from(groups.values());
};

function Tracking(props) {
  const {
    t, sale, reload,
  } = props;

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [familyReturnItem, setFamilyReturnItem] = useState(null);
  const [restockModalOpen, setRestockModalOpen] = useState(false);
  const [restockModalData, setRestockModalData] = useState(null);
  const [restockLabelQty, setRestockLabelQty] = useState(1);
  const [inspectModalOpen, setInspectModalOpen] = useState(false);
  const [inspectModalData, setInspectModalData] = useState(null);
  const [packages, setPackages] = useState([]);
  const [activeTab, setActiveTab] = useState('1');
  const [exports, setExports] = useState([]);
  const [shippedItems, setShippedItems] = useState([]);
  const [signal, setSignal] = useState(0);
  const saleItemIds = sale?.items?.map((item) => item.id) || [];
  const modalGroupedContents = modalData !== null
    ? buildPackageContentGroups(
      modalData.contents
        .map((content) => ({
          ...content,
          package_tracking_number: modalData.tracking_number,
        })),
      sale.id,
    )
    : [];
  const packagedContents = buildPackageContentGroups(
    packages.map((pkg) => pkg.contents
      .filter((content) => saleItemIds.includes(content.order_export_item.sale_item_id))
      .map((content) => ({
        ...content,
        package_tracking_number: pkg.tracking_number,
      }))).flat(),
  );
  const packageSummaryRows = sale?.items?.map((saleItem) => {
    const packagedGroup = packagedContents.find(
      (group) => group.saleItemId === saleItem.id,
    );
    const listingItems = Array.isArray(saleItem.listing?.items) ? saleItem.listing.items : [];
    const packagedQty = packagedGroup !== undefined && packagedGroup.parts.length > 0
      ? packagedGroup.parts.reduce(
        (minQty, part) => {
          const matchedListingItem = listingItems.find(
            (listingItem) => Number(listingItem.id) === Number(part.componentItemId),
          );
          const requiredQty = Number(matchedListingItem?.item_qty) || 1;
          const listingEquivalentQty = Math.floor((Number(part.qty) || 0) / requiredQty);

          return Math.min(minQty, listingEquivalentQty);
        },
        Number.POSITIVE_INFINITY,
      )
      : 0;
    const soldQty = Number(saleItem.qty) || 0;
    let status = 'needed';
    const packedBreakdown = packagedGroup !== undefined
      ? packagedGroup.parts.flatMap((part) => {
        const rows = [];
        const originalQty = part.qty - part.subQty;

        if (originalQty > 0) {
          rows.push({
            key: `${part.key}-original`,
            label: part.summary,
            qty: originalQty,
            isSub: false,
          });
        }

        if (part.subQty > 0) {
          rows.push({
            key: `${part.key}-sub`,
            label: part.summary,
            qty: part.subQty,
            isSub: true,
            subFor: saleItem.listing?.sku || t('N/A'),
          });
        }

        return rows;
      })
      : [];

    if (packagedQty === soldQty) {
      status = 'match';
    } else if (packagedQty > soldQty) {
      status = 'over';
    }

    return {
      id: saleItem.id,
      sku: saleItem.listing?.sku || t('N/A'),
      packagedQty,
      soldQty,
      packedBreakdown,
      trackingNumbers: packagedGroup?.trackingNumbers || [],
      status,
    };
  }) || [];
  const getSaleScopedPackageQty = (pkg) => pkg.contents
    .filter((content) => Number(content.sale_id) === Number(sale.id))
    .map((content) => Number(content.item_qty) || 0)
    .reduce((acc, curr) => acc + curr, 0);
  const hasOtherOrderContents = (pkg) => pkg.contents
    .some((content) => Number(content.sale_id) !== Number(sale.id));
  const getOtherOrderReferences = (pkg) => (
    [...new Set(
      pkg.contents
        .filter((content) => Number(content.sale_id) !== Number(sale.id))
        .map((content) => content.order_export_item.sale_item?.sale?.reference1 || `#${content.sale_id}`),
    )]
  );

  const getPackages = async () => {
    try {
      const results = await get(`/api/sales/packages/by-sale/${sale.id}`);

      setPackages(results);

      // create array of all unique items shipped, reconstituting kits as needed
      const itms = [];
      results.forEach((x) => {
        x.contents.forEach((y) => {
          const found = itms.find((w) => w.item.id === y.order_export_item.item.id);
          const foundSaleItem = itms.find(
            (w) => w.sale_item?.id === y.order_export_item.sale_item?.id,
          );

          if (found === undefined) {
            itms.push({
              item: y.order_export_item.item,
              lots: y.lot != null && y.lot !== '' ? [y.lot] : [],
              sale_item: y.order_export_item.sale_item,
              sub: (y.order_export_item.original_substituted_item_id !== null),
            });
          } else if (foundSaleItem === undefined && y.order_export_item.sale_item) {
            itms.push({
              item: y.order_export_item.item,
              lots: y.lot != null && y.lot !== '' ? [y.lot] : [],
              sale_item: y.order_export_item.sale_item,
              sub: (y.order_export_item.original_substituted_item_id !== null),
            });
          }

          if (found !== undefined && y.lot != null && y.lot !== '' && !found.lots.includes(y.lot)) {
            found.lots.push(y.lot);
          }

          if (y.order_export_item.original_substituted_item_id !== null) {
            if (found !== undefined) {
              found.sub = true;
            }
          }
        });
      });

      setShippedItems(itms);
    } catch (e) {
      toastr.error(e.message);
    }
  };

  const getExports = async () => {
    try {
      const results = await get(`/api/sales/distribution?f=sale_id%3D${sale.id}`);

      setExports(results.results);
    } catch (e) {
      toastr.error(e.message);
    }
  };

  useEffect(() => {
    getPackages();

    if (sale !== null) {
      getExports();
    }
  }, [sale]);

  useEffect(() => {
    setSignal(signal + 1);
  }, [reload]);

  useEffect(() => {
    const dt = new Date(Date.now());

    dt.setDate(dt.getDate() - 7);

    // if first package is older than 7 days, switch to return tab
    if (packages.length > 0 && moment(packages[0].created_at).isBefore(dt)) {
      setActiveTab('2');
    }
  }, [packages]);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const printRestock = async (item, saleItem, qty) => {
    try {
      const remoteData = await get(`/api/burnsville/inventoryByItemId/${item.id}`);
      const dateCode = moment().format('YYYYMMDD');

      const lot = `RTGU${dateCode}`;

      let suffix = '01';

      if (packages.length > 0) {
        // breaks the packages down to a simple array of return lot strings
        const rtrnLots = packages.map((x) => x.contents
          .filter((y) => y.order_export_item.sale_item_id === saleItem.id)
          .filter((y) => y.lot && (y.lot.startsWith('RTGU') || y.lot.startsWith('RTXX')))
          .map((y) => y.lot)).flat();

        if (rtrnLots.length > 0) {
          // pick first return lot - what else can you do
          // when there's multi qty and they return a subset of qty.
          const mtch = rtrnLots[0].match(/^RT.{2}[0-9]{8}([0-9]{2})$/);

          if (mtch !== null) {
            // append increment after date code
            suffix = `${(parseInt(mtch[1], 10) + 1).toString().padStart(2, '0')}`;
          }
        }
      }

      await post(`/api/burnsville/printDZLabel/${remoteData.stock_id}?qty=${qty}`, {
        lot: `${lot}${suffix}`,
        part_number: remoteData.part_number,
      });

      await post('/api/user/actions/create', {
        action: 'printRestock',
        entity: 'saleItem',
        entityId: saleItem.id,
      });

      toastr.info('Restock printed');
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        toastr.error(error.response.data.error);
      } else {
        toastr.error(error.message);
      }
    }
  };

  const printDefective = async (item, saleItem) => {
    try {
      const remoteData = await get(`/api/burnsville/inventoryByItemId/${item.id}`);
      const dateCode = moment().format('YYYYMMDD');

      const lot = `RTXX${dateCode}`;

      let suffix = '01';

      if (packages.length > 0) {
        // breaks the packages down to a simple array of return lot strings
        const rtrnLots = packages.map((x) => x.contents
          .filter((y) => y.order_export_item.sale_item_id === saleItem.id)
          .filter((y) => y.lot && (y.lot.startsWith('RTGU') || y.lot.startsWith('RTXX')))
          .map((y) => y.lot)).flat();

        if (rtrnLots.length > 0) {
          // pick first return lot - what else can you do
          // when there's multi qty and they return a subset of qty.
          const mtch = rtrnLots[0].match(/^RT.{2}[0-9]{8}([0-9]{2})$/);

          if (mtch !== null) {
            // append increment after date code
            suffix = `${(parseInt(mtch[1], 10) + 1).toString().padStart(2, '0')}`;
          }
        }
      }

      await post(`/api/burnsville/printDZLabel/${remoteData.stock_id}`, {
        lot: `${lot}${suffix}`,
        part_number: remoteData.part_number,
      });

      await post('/api/user/actions/create', {
        action: 'printDefective',
        entity: 'saleItem',
        entityId: saleItem.id,
      });

      toastr.info('Defective printed');
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        toastr.error(error.response.data.error);
      } else {
        toastr.error(error.message);
      }
    }
  };

  const jsx = (
    <div>
      {packages.length === 0 && (
        <Alert color="warning text-dark">{t('No tracking yet')}</Alert>
      )}

      {packages.length > 0 && (
        <>
          <Nav tabs style={{ borderBottom: 'none' }}>
            <NavItem style={{ borderBottom: 'none' }}>
              <NavLink
                style={{ cursor: 'pointer' }}
                className={classnames({
                  active: activeTab === '1',
                })}
                onClick={() => {
                  toggle('1');
                }}
              >
                {t('Outbound')}
              </NavLink>
            </NavItem>
            <NavItem style={{ borderBottom: 'none' }}>
              <NavLink
                style={{ cursor: 'pointer' }}
                className={classnames({
                  active: activeTab === '2',
                })}
                onClick={() => {
                  toggle('2');
                }}
              >
                {t('Return')}
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent
            activeTab={activeTab}
            className="card border-top-0 rounded-top-0 p-3 text-muted mb-3"
          >
            <TabPane tabId="1">
              {packageSummaryRows.length > 0 && (
                <div
                  className="table-responsive mb-3"
                  style={{ backgroundColor: 'inherit' }}
                >
                  <table
                    className="table table-bordered table-striped mb-0"
                    style={{ backgroundColor: 'inherit' }}
                  >
                    <thead>
                      <tr>
                        <th>{t('Sold Sku')}</th>
                        <th>{t('Sold')}</th>
                        <th>{t('Packed')}</th>
                        <th>{t('Packed Breakdown')}</th>
                        <th>{t('Tracking')}</th>
                        <th>{t('Status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {packageSummaryRows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.sku}</td>
                          <td>{row.soldQty}</td>
                          <td>{row.packagedQty}</td>
                          <td>
                            {row.packedBreakdown.length > 0 && (
                              <div className="vstack gap-1">
                                {row.packedBreakdown.map((entry) => (
                                  <div key={entry.key}>
                                    <span>{entry.label}</span>
                                    {' x'}
                                    {entry.qty}
                                    {entry.isSub && (
                                      <>
                                        {' '}
                                        <Badge
                                          color="secondary"
                                          pill
                                          style={{
                                            backgroundColor: '#5f6672',
                                            color: '#f8f9fa',
                                          }}
                                        >
                                          {t('Sub')}
                                        </Badge>
                                        {' '}
                                        <small style={{ color: '#cfd4da' }}>
                                          {t('for')}
                                          {' '}
                                          {entry.subFor}
                                        </small>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {row.packedBreakdown.length === 0 && (
                              <span className="text-muted">{t('Nothing packed yet')}</span>
                            )}
                          </td>
                          <td>
                            {row.trackingNumbers.length > 0 && (
                              <div className="vstack gap-1">
                                {row.trackingNumbers.map((trackingNumber) => (
                                  <div key={trackingNumber}>{trackingNumber}</div>
                                ))}
                              </div>
                            )}
                            {row.trackingNumbers.length === 0 && (
                              <span className="text-muted">{t('N/A')}</span>
                            )}
                          </td>
                          <td className="align-middle">
                            {row.status === 'match' && (
                              <span className="text-success" title={t('Match')}>
                                <span className="mdi mdi-checkbox-marked-outline fs-5" />
                              </span>
                            )}
                            {row.status === 'needed' && (
                              <span className="text-warning" title={t('Still needed')}>
                                <span className="mdi mdi-checkbox-blank-outline fs-5" />
                              </span>
                            )}
                            {row.status === 'over' && (
                              <span className="text-danger" title={t('Over packed')}>
                                <span className="mdi mdi-alert-circle-outline fs-5" />
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>{t('Carrier')}</th>
                      <th>{t('Service')}</th>
                      <th>{t('Tracking')}</th>
                      <th>{t('Exported')}</th>
                      <th>{t('Imported')}</th>
                      <th>{t('Cost')}</th>
                      <th>{t('Items')}</th>
                      <th>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packages.map((row) => (
                      <tr key={row.id}>
                        <td className="align-middle">{row.carrier?.toUpperCase()}</td>
                        <td className="align-middle">{row.service?.toUpperCase()}</td>
                        <td className="align-middle">
                          {row.tracking_number}
                          {row.contents.filter((x) => x.lot && (x.lot.startsWith('RTGU') || x.lot.startsWith('RTXX'))).length > 0 && (
                            <Alert color="warning" className="text-dark p-1 mt-1 mb-0 fs-6 border-success">
                              {t('Re-shipped previously returned parts!')}
                            </Alert>
                          )}
                        </td>
                        <td className="align-middle">
                          {row.sale_exported && (
                            <span className="mdi mdi-checkbox-marked-outline text-success fs-3" />
                          )}

                          {!row.sale_exported && (
                            <span className="mdi mdi-checkbox-blank-outline text-default fs-3" />
                          )}
                        </td>
                        <td className="align-middle">{moment(row.created_at).fromNow()}</td>
                        <td className="align-middle">
                          {
                            (new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                            }).format(row.cost_usd))
                          }
                        </td>
                        <td className="align-middle">
                          {
                            row.contents.map((x) => x.item_qty)
                              .reduce((acc, curr) => acc + curr, 0)
                          }
                          {hasOtherOrderContents(row) && (
                            <div className="mt-1">
                              <Badge color="warning" className="text-dark">
                                {`${getSaleScopedPackageQty(row)} ${t('for this order')} / ${row.contents.map((x) => x.item_qty)
                                  .reduce((acc, curr) => acc + curr, 0)} ${t('total in package')}`}
                              </Badge>
                              <div className="mt-1 fs-6 text-muted">
                                {t('Also contains')}
                                {': '}
                                {getOtherOrderReferences(row).join(', ')}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="align-middle">
                          <Button color="secondary" size="sm" className="btn-soft-secondary" onClick={() => { setModalData(row); setModalOpen(true); }}>
                            <i className="mdi mdi-eye" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabPane>

            <TabPane tabId="2">
              {packages.filter((x) => x.contents.filter((y) => y.lot && (y.lot.startsWith('RTGU') || y.lot.startsWith('RTXX'))).length > 0).length > 0 && (
                <Alert color="warning" className="text-dark border-success p-2 mt-1 mb-2">
                  {t('Re-shipped previously returned parts!')}
                </Alert>
              )}
              <div className="table-responsive">
                <Table bordered hover>
                  <thead>
                    <tr>
                      <th>{t('Thumbnail')}</th>
                      <th>{t('Sku')}</th>
                      <th>{t('Item')}</th>
                      <th>{t('Qty/Scrap')}</th>
                      <th>{t('Condition')}</th>
                      <th>{t('Alert')}</th>
                      <th>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sale.items.map((row) => (
                      <React.Fragment key={`${row.id}`}>
                        <tr>
                          <td className="align-middle" colSpan="7">
                            <Link to={`/listing/${row.listing.id}`} target="_blank" rel="noreferrer">{row.listing.title}</Link>
                          </td>
                        </tr>
                        {shippedItems
                          .filter((x) => x.sale_item.id === row.id).map((entry) => (
                            <tr key={`${entry.sale_item.id}${entry.item.id}`}>
                              <td className="align-middle"><img src={`/api/items/thumbnail/${entry.item.id}`} alt="thumbnail" style={{ maxWidth: '64px', maxHeight: '64px' }} /></td>
                              <td className="align-middle">
                                {entry.sale_item.listing.sku}
                                {packages.filter((y) => y.contents.filter((x) => x.order_export_item.sale_item_id === entry.sale_item.id && x.order_export_item.item_id === entry.item.id && x.lot && (x.lot.startsWith('RTGU') || x.lot.startsWith('RTXX'))).length > 0).length > 0 && (
                                  <Alert color="warning" className="fs-6 p-1 mt-1 mb-0 border-success">
                                    <span className="mdi mdi-close-thick" />
                                    {' '}
                                    {t('Previous Return')}
                                  </Alert>
                                )}
                                <div className="mt-1">
                                  {entry.lots.map((x) => (
                                    <div>{x}</div>
                                  ))}
                                </div>
                              </td>
                              <td className="align-middle">
                                <Link to={`/item/${entry.item.id}`} target="_blank" rel="noreferrer">
                                  {entry.item.part_number}
                                </Link>
                                {entry.sub && (
                                  <div>
                                    <Badge color="danger">{t('Sub')}</Badge>
                                  </div>
                                )}
                              </td>
                              <td className={`align-middle${entry.item.qty >= parseInt(entry.item.scrap, 10) ? ' text-danger' : ''}`}>{`${entry.item.qty}/${entry.item.scrap}`}</td>
                              <td className="align-middle">{entry.item.condition}</td>
                              <td className="align-middle">{entry.item.alert}</td>
                              <td className="align-middle">
                                <div className="hstack gap-1">
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    className="btn-soft-secondary"
                                    onClick={() => {
                                      setInspectModalData(entry.item);
                                      setInspectModalOpen(true);
                                    }}
                                  >
                                    <i className="mdi mdi-eye" />
                                    {' '}
                                    {t('Inspect')}
                                  </Button>
                                  <Button
                                    color="light"
                                    size="sm"
                                    disabled={
                                      exports.filter((x) => x.destination.includes('burnsville')
                                        && x.items.map(
                                          (y) => y.sale_item_id,
                                        ).includes(entry.sale_item.id)).length === 0
                                    }
                                    className="btn-soft-secondary"
                                    style={{
                                      textDecoration: exports.filter((x) => x.destination.includes('burnsville')
                                        && x.items.map(
                                          (y) => y.sale_item_id,
                                        ).includes(entry.sale_item.id)).length === 0 ? 'line-through' : 'none',
                                    }}
                                    onClick={() => printDefective(entry.item, entry.sale_item)}
                                  >
                                    <i className="mdi mdi-printer" />
                                    {' '}
                                    {t('Defective')}
                                  </Button>
                                </div>
                                <div className="hstack gap-1 mt-1">
                                  <Button
                                    color="light"
                                    size="sm"
                                    disabled={
                                      exports.filter((x) => x.destination.includes('burnsville')
                                        && x.items.map(
                                          (y) => y.sale_item_id,
                                        ).includes(entry.sale_item.id)).length === 0
                                    }
                                    style={{
                                      textDecoration: exports.filter((x) => x.destination.includes('burnsville')
                                        && x.items.map(
                                          (y) => y.sale_item_id,
                                        ).includes(entry.sale_item.id)).length === 0 ? 'line-through' : 'none',
                                    }}
                                    className="btn-soft-secondary"
                                    onClick={() => printRestock(entry.item, entry.sale_item, 1)}
                                  >
                                    <i className="mdi mdi-printer" />
                                    {' '}
                                    {t('Restock')}
                                  </Button>
                                  <Button
                                    color="light"
                                    size="sm"
                                    disabled={
                                      exports.filter((x) => x.destination.includes('burnsville')
                                        && x.items.map(
                                          (y) => y.sale_item_id,
                                        ).includes(entry.sale_item.id)).length === 0
                                    }
                                    style={{
                                      textDecoration: exports.filter((x) => x.destination.includes('burnsville')
                                        && x.items.map(
                                          (y) => y.sale_item_id,
                                        ).includes(entry.sale_item.id)).length === 0 ? 'line-through' : 'none',
                                    }}
                                    className="btn-soft-secondary"
                                    onClick={() => {
                                      // eslint-disable-next-line camelcase
                                      const { item, sale_item } = entry;
                                      // eslint-disable-next-line camelcase
                                      setRestockModalData({ item, sale_item });
                                      setRestockLabelQty(1);
                                      setRestockModalOpen(true);
                                    }}
                                  >
                                    {t('Restock Multiple')}
                                  </Button>
                                </div>
                                <div className="mt-1">
                                  <Button
                                    color="light"
                                    size="sm"
                                    className="btn-soft-secondary"
                                    onClick={() => setFamilyReturnItem(entry.sale_item)}
                                  >
                                    <i className="mdi mdi-printer" />
                                    {' '}
                                    {t('Family Return')}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            </TabPane>
          </TabContent>
        </>
      )}

      {familyReturnItem && (
        <FamilyReturnLabelModal
          saleItem={familyReturnItem}
          onClose={() => setFamilyReturnItem(null)}
        />
      )}

      <Modal
        size="md"
        isOpen={inspectModalOpen}
      >
        <div className="modal-header">
          <h5
            className="modal-title mt-0"
            id="inspectModal"
          >
            {t('Item Details')}
            {modalData !== null && modalData.id}
          </h5>
          <button
            onClick={() => { setInspectModalOpen(false); }}
            type="button"
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <Inspector item={inspectModalData} />
        </div>
        <div className="modal-footer">
          <button
            type="button"
            onClick={() => { setInspectModalOpen(false); }}
            className="btn btn-soft-dark"
            data-dismiss="modal"
          >
            {t('Close')}
          </button>
        </div>
      </Modal>

      <Modal
        size="md"
        isOpen={restockModalOpen}
      >
        <div className="modal-header">
          <h5
            className="modal-title mt-0"
            id="restockModal"
          >
            {t('Item Details')}
            {restockModalData !== null && restockModalData.id}
          </h5>
          <button
            onClick={() => { setRestockModalOpen(false); }}
            type="button"
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          {restockModalData && (
            <div className="mb-3">
              <label htmlFor="labelCopies" className="form-label">{t('Number of copies')}</label>
              <div className="input-group">
                <input
                  type="number"
                  className="form-control text-center"
                  id="labelCopies"
                  min="1"
                  defaultValue={restockLabelQty}
                  onChange={(e) => {
                    setRestockLabelQty(parseInt(e.target.value, 10));
                  }}
                />
              </div>
              <small className="text-muted">{t('How many copies of this label to print')}</small>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            onClick={() => { setRestockModalOpen(false); }}
            className="btn btn-soft-dark"
            data-dismiss="modal"
          >
            {t('Close')}
          </button>
          <button
            type="button"
            onClick={() => {
              printRestock(restockModalData.item, restockModalData.sale_item, restockLabelQty);
              setRestockModalOpen(false);
            }}
            disabled={Number.isNaN(restockLabelQty) || restockLabelQty < 1}
            className="btn btn-soft-dark"
            data-dismiss="modal"
          >
            {t('Print')}
          </button>
        </div>
      </Modal>

      <Modal
        size="xl"
        isOpen={modalOpen}
      >
        <div className="modal-header">
          <h5
            className="modal-title mt-0"
            id="myLargeModalLabel"
          >
            {t('Contents of package #')}
            {modalData !== null && modalData.id}
          </h5>
          <button
            onClick={() => { setModalOpen(false); }}
            type="button"
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div className="modal-body">
          <div>
            <Row>
              <Col xl="6">
                <table className="table table-bordered">
                  <tbody>
                    {modalData !== null && (
                      <Fragment key={modalData.id}>
                        <tr>
                          <th>{t('Carrier')}</th>
                          <td>{modalData.carrier?.toUpperCase()}</td>
                        </tr>
                        <tr>
                          <th>{t('Service')}</th>
                          <td>{modalData.service?.toUpperCase()}</td>
                        </tr>
                        <tr>
                          <th>{t('Tracking')}</th>
                          <td>{modalData.tracking_number || ''}</td>
                        </tr>
                      </Fragment>
                    )}
                  </tbody>
                </table>
              </Col>
              <Col xl="6">
                <table className="table table-bordered">
                  <tbody>
                    {modalData !== null && (
                      <Fragment key={modalData.id}>
                        <tr>
                          <th>{t('Dimensions (in.)')}</th>
                          <td>{modalData.dimensions}</td>
                        </tr>
                        <tr>
                          <th>{t('Weight (lbs)')}</th>
                          <td>{modalData.weight_lbs}</td>
                        </tr>
                        <tr>
                          <th>{t('Cost (USD)')}</th>
                          <td>{modalData.cost_usd}</td>
                        </tr>
                      </Fragment>
                    )}
                  </tbody>
                </table>
              </Col>
            </Row>
          </div>

          <div className="mb-3">
            <h5>
              {t('Packaged By')}
              {': '}
              {modalData !== null && modalData.contents.length > 0 && (
                <span>{modalData.contents[0].order_export_item.order_export.destination}</span>
              )}
              {' @ '}
              <em style={{ fontWeight: '100' }}>
                {modalData !== null && (
                  <span>
                    {
                      (moment(modalData.contents[0].order_export_item.order_export.created_at))
                        .toLocaleString()
                    }
                  </span>
                )}
              </em>
            </h5>
          </div>

          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                <th>{t('Order')}</th>
                <th>{t('Listing')}</th>
                <th>{t('Part Number')}</th>
                <th>{t('Qty')}</th>
                <th>{t('Value (USD)')}</th>
                <th>{t('Lots')}</th>
              </tr>
            </thead>
            <tbody>
              {modalData !== null && modalGroupedContents.map((row) => (
                <Fragment key={row.key}>
                  {row.parts.map((part, index) => {
                    const partLots = Object.entries(part.lots);

                    return (
                      <tr key={part.key} className={!row.isCurrentSale ? 'table-warning' : ''}>
                        {index === 0 && (
                          <td rowSpan={row.parts.length} className="align-top">
                            {row.isCurrentSale ? (
                              <Badge color="success">{t('This Order')}</Badge>
                            ) : (
                              <div className="vstack gap-1">
                                <Badge color="warning" className="text-dark">{t('Other Order')}</Badge>
                                <span>{row.saleReference}</span>
                              </div>
                            )}
                          </td>
                        )}
                        {index === 0 && (
                          <td rowSpan={row.parts.length} className="align-top">
                            {row.listing ? (
                              <div className="vstack gap-1">
                                <div>{row.listing.title}</div>
                                <div>
                                  <span>{t('Sku')}</span>
                                  {': '}
                                  <Link to={`/listing/${row.listing.id}`}>
                                    {row.listing.sku}
                                  </Link>
                                </div>
                              </div>
                            ) : (
                              <span>{t('Listing unavailable')}</span>
                            )}
                          </td>
                        )}
                        <td>{part.description}</td>
                        {index === 0 && (
                          <td rowSpan={row.parts.length} className="align-top">
                            {row.qty}
                          </td>
                        )}
                        {index === 0 && (
                          <td rowSpan={row.parts.length} className="align-top">
                            {usdFormatter.format(row.valueUsd)}
                          </td>
                        )}
                        <td>
                          {partLots.length > 0 ? (
                            <div className="vstack gap-1">
                              {partLots.map(([lot, qty]) => (
                                <div key={lot}>
                                  {lot}
                                  {' x'}
                                  {qty}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span>{t('N/A')}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            onClick={() => { setModalOpen(false); }}
            className="btn btn-soft-dark"
            data-dismiss="modal"
          >
            {t('Close')}
          </button>
        </div>
      </Modal>
    </div>
  );

  return jsx;
}

Tracking.defaultProps = {
  sale: {},
  reload: 0,
};

Tracking.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
  sale: PropTypes.object,
  reload: PropTypes.number,
};

export default connect(() => ({

}), {})(withTranslation()(Tracking));
